import { useCallback, useEffect, useState } from 'react';
import { API } from '../../helpers';
import { reconcileOrganizationModelsV812 } from './organizationEditorV812';
import {
  normalizeOrganizationModelsV104,
  organizationModelModeV104,
} from './organizationModelsV104';

export default function useOrganizationScopeV104({
  open,
  hydratedIdentity,
  editorIdentity,
  groups,
  editing,
  paused,
  setForm,
}) {
  const groupKey = JSON.stringify(normalizeOrganizationModelsV104(groups));
  const key = `${editorIdentity}:${groupKey}`;
  const [revision, setRevision] = useState(0);
  const [scope, setScope] = useState({
    key: '',
    models: [],
    loading: false,
    error: false,
  });
  const refresh = useCallback(() => setRevision((value) => value + 1), []);
  const active = open && hydratedIdentity === editorIdentity && !paused;

  useEffect(() => {
    if (!active) return undefined;
    const selectedGroups = JSON.parse(groupKey);
    if (!selectedGroups.length) {
      setScope({ key, models: [], loading: false, error: false });
      return undefined;
    }
    let cancelled = false;
    setScope((current) => ({
      key,
      models: current.key === key ? current.models : [],
      loading: true,
      error: false,
    }));
    const load = async () => {
      try {
        const query = selectedGroups
          .map((group) => `group=${encodeURIComponent(group)}`)
          .join('&');
        const response = await API.get(
          `/api/billing/v8/admin/organization-scope?${query}`
        );
        if (cancelled) return;
        if (
          !response?.data?.success ||
          !Array.isArray(response.data.data?.models) ||
          response.data.data.models.some(
            (model) => typeof model !== 'string' || !model
          )
        ) {
          throw new Error('Invalid organization scope');
        }
        const models = normalizeOrganizationModelsV104(
          response.data.data.models
        );
        // Preserve hidden exclusions and observe each new model once, without replacing the draft.
        setForm((current) => {
          const exclusions = reconcileOrganizationModelsV812({
            currentExcluded: current.excluded_models,
            knownModels: current.known_models,
            routableModels: models,
            editing:
              editing ||
              ['rules', 'all_current'].includes(current.model_access_mode),
          });
          return {
            ...current,
            excluded_models: exclusions,
            model_access_mode: organizationModelModeV104(
              current.model_access_mode,
              models,
              exclusions
            ),
            known_models: normalizeOrganizationModelsV104([
              ...current.known_models,
              ...models,
            ]),
          };
        });
        setScope({ key, models, loading: false, error: false });
      } catch (_) {
        if (!cancelled)
          setScope((current) => ({ ...current, loading: false, error: true }));
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [active, groupKey, key, editing, revision, setForm]);

  useEffect(() => {
    if (!active || groupKey === '[]') return undefined;
    const refreshVisible = () => {
      if (!document.hidden) refresh();
    };
    const timer = window.setInterval(refreshVisible, 30000);
    window.addEventListener('focus', refreshVisible);
    window.addEventListener('pageshow', refreshVisible);
    document.addEventListener('visibilitychange', refreshVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshVisible);
      window.removeEventListener('pageshow', refreshVisible);
      document.removeEventListener('visibilitychange', refreshVisible);
    };
  }, [active, groupKey, refresh]);

  const matched = active && scope.key === key;
  return {
    models: matched ? scope.models : [],
    loading: active && (!matched || scope.loading),
    error: matched && scope.error,
    ready: matched && !scope.loading && !scope.error,
    refresh,
  };
}
